import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy } from '@angular/core';
import type { Editor } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
import { StringValueAccessor } from './value-accessors/string-value-accessor';
import { CkEditorLease, CkEditorPoolService } from './ckeditor-pool.service';
import { WysiwygBase64Sanitizer } from '../util/wysiwyg-base64-sanitizer';
import { debounce } from 'lodash-decorators';
import { restoreStyleBlocks } from './ckeditor-content-style';
import { inlineStyleBlocks } from './ckeditor-content-style-inline';

/**
 * Shared behaviour for wysiwyg wrappers that borrow an editor from CkEditorPoolService instead of
 * creating their own, so a host coming and going (tabs, dialogs, navigation) is not a new billable load.
 */
@Directive()
export abstract class PooledCkEditorHost extends StringValueAccessor implements AfterViewInit, OnDestroy {
  public abstract blockEmbeddedBase64Images: boolean;
  public abstract change: EventEmitter<any>;
  public abstract ready: EventEmitter<Event>;
  public abstract blur: EventEmitter<Event>;
  public abstract focus: EventEmitter<Event>;
  /**
   * The HTML is sent as email: size images with explicit widths and add align attributes for email clients.
   * Also turns on the inline-style export (on blur and in flushInlineStyles) and its <style> restoration, and the
   * export inlines the content's <style> rules onto the elements they style (ATLAS-5883); other hosts save the editor's own data (ATLAS-5879).
   */
  @Input() public emailContent: boolean = false;

  public ckEditorReady = false;
  public editorInstance: Editor | null = null;
  protected _savedSelectionRange: any = null;

  private _lease: CkEditorLease | null = null;
  private _destroyed = false;
  private _lastExportedValue: string | null = null;

  protected constructor(protected _ckEditorPool: CkEditorPoolService) {
    super();
  }

  /** The element the pooled editor UI is moved into. */
  protected abstract get editorHostElement(): ElementRef<HTMLElement> | undefined;

  /** Whether the editor should currently be read-only. */
  protected abstract get editorReadOnly(): boolean;

  ngAfterViewInit(): void {
    this._ckEditorPool.acquire().then(
      (lease) => {
        if (this._destroyed || !this.editorHostElement) {
          this._ckEditorPool.release(lease);
          return;
        }
        this._attach(lease);
      },
      (err) => console.error('[ckeditor] failed to load editor', err)
    );
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    (this._exportInlineStyles as any).cancel?.();
    this._ckEditorPool.release(this._lease);
    this._lease = null;
    this.editorInstance = null;
    this._savedSelectionRange = null;
    this._lastExportedValue = null;
  }

  writeValue(value: string): void {
    if (this.blockEmbeddedBase64Images) {
      value = WysiwygBase64Sanitizer.sanitizeInitialValue(value, true);
    }
    super.writeValue(value);
    // the outer form echoing back our own inlined export is not an outside change; re-setting the
    // editor data would replace the content and reset the selection (ATLAS-5734)
    if (value !== this._lastExportedValue) {
      this._syncEditorData();
    }
  }

  /** Re-applies the current read-only state; call when the inputs that drive it change. */
  protected _updateReadOnly(): void {
    this._lease?.setReadOnly(this.editorReadOnly);
  }

  /** Runs when this host's editor goes back to the pool, e.g. to take back DOM moved into the editor UI. */
  protected _onEditorRelease(cleanup: () => void): void {
    this._lease?.onRelease(cleanup);
  }

  /** Inserts HTML at the last caret position the user left in the editor. */
  protected _insertHtmlAtSavedSelection(html: string): void {
    const editor = this.editorInstance;
    if (!editor) return;
    editor.editing.view.focus();
    if (this._savedSelectionRange) {
      editor.model.change((writer) => {
        writer.setSelection(this._savedSelectionRange);
      });
    }
    const viewFragment = editor.data.processor.toView(html);
    const modelFragment = editor.data.toModel(viewFragment);
    editor.model.insertContent(modelFragment);
  }

  /**
   * handle editor's change event
   */
  public handleEditorChangeEvent(changeEvent: any): void {
    this.change.emit(changeEvent);
  }

  /**
   * handle editor's ready event
   */
  public handleEditorReadyEvent(editor: Editor): void {
    this.ready.emit();
    this.editorInstance = editor;
    WysiwygBase64Sanitizer.attachImageBlockingEvents(editor, this.blockEmbeddedBase64Images, (target, event, callback, options) =>
      this._lease?.listen(target, event, callback, options)
    );
  }

  /**
   * handle editor's blur event
   */
  public handleEditorBlurEvent($event: any): void {
    this.blur.emit($event);
    if (this.editorInstance) {
      const range = this.editorInstance.model.document.selection.getFirstRange();
      if (range) {
        this._savedSelectionRange = range;
      }
    }
    this._exportInlineStyles();
  }

  /**
   * handle editor's focus event
   */
  public handleEditorFocusEvent($event: any): void {
    (this._exportInlineStyles as any).cancel();
    this.focus.emit($event);
  }

  /**
   * Runs the inline-style export now and pushes the result to the outer form. Await this before
   * saving or sending: the blur-triggered export is debounced and async, so a Save click right
   * after an edit (or an edit made where the view never blurs, e.g. source editing) would
   * otherwise persist the raw editor data without its inlined CSS.
   * The export, and so the emit, happens only for `emailContent` hosts (ATLAS-5879). On any other host
   * this only leaves source editing, which commits the source edits through the change handler, and resolves.
   */
  public flushInlineStyles(): Promise<void> {
    (this._exportInlineStyles as any).cancel?.();
    this._leaveSourceEditing();
    return this._runInlineStyleExport();
  }

  @debounce(150)
  protected _exportInlineStyles(): Promise<void> {
    return this._runInlineStyleExport();
  }

  private async _runInlineStyleExport(): Promise<void> {
    // ATLAS-5879: only email content is inlined; read when the export runs so a scheduled blur export honours a later flip
    if (!this.emailContent) return;
    const editor = this.editorInstance;
    if (!editor) return;
    try {
      // ATLAS-5793: the editor's own data is the source of the <style> blocks the export drops; capture it before the await
      const source: string = (editor as any).data?.get?.() ?? '';
      let inlined = (await (editor as any).execute('exportInlineStyles')) as string;
      // the editor may have been returned to the pool while exporting
      if (!inlined || editor !== this.editorInstance) return;
      inlined = restoreStyleBlocks(source, inlined);
      inlined = WysiwygBase64Sanitizer.cleanExportedImageStyles(inlined, this.emailContent);
      // ATLAS-5883: after the root-default strip, so an inlined value is never stripped; mail clients may drop <style>
      inlined = inlineStyleBlocks(inlined);
      // hand the inlined HTML to the outer form only; writing it back into the editor would
      // replace the content and reset the selection, moving inserted tables (ATLAS-5734)
      if (inlined && inlined !== this._lastExportedValue) {
        this._lastExportedValue = inlined;
        this.onChange(inlined);
      }
    } catch {}
  }

  private _attach(lease: CkEditorLease): void {
    this._lease = lease;
    const editor = lease.editor;
    lease.attach(this.editorHostElement!.nativeElement);
    lease.setData(this._value ?? '');
    lease.setReadOnly(this.editorReadOnly);
    lease.onChange((data, evt) => {
      // a non-email host never runs the inline-style export, so this value is what gets saved; email content is replaced by that export
      this.value = this.emailContent ? data : WysiwygBase64Sanitizer.addImageLayoutStyles(data);
      this.handleEditorChangeEvent({ event: evt, editor });
    });
    lease.onFocus((evt) => this.handleEditorFocusEvent({ event: evt, editor } as any));
    lease.onBlur((evt) => this.handleEditorBlurEvent({ event: evt, editor } as any));
    this.ckEditorReady = true;
    this.handleEditorReadyEvent(editor);
  }

  /** Switches back from source editing, as the toolbar button does, so the export sees the source edits. */
  private _leaveSourceEditing(): void {
    const editor: any = this.editorInstance;
    if (!editor) return;
    for (const pluginName of ['SourceEditing', 'SourceEditingEnhanced']) {
      const plugin: any = editor.plugins.has(pluginName) ? editor.plugins.get(pluginName) : null;
      if (plugin?.isSourceEditingMode) {
        plugin.isSourceEditingMode = false;
      }
    }
  }

  /** Pushes the model value into the editor when it was changed from outside the editor. */
  private _syncEditorData(): void {
    if (this._lease && (this._value ?? '') !== this._lease.getData()) {
      this._lease.setData(this._value ?? '');
    }
  }
}
